import { useState, useEffect, useCallback, useMemo } from 'react';
import { format, eachDayOfInterval, startOfMonth, endOfMonth } from 'date-fns';
import {
  validateDayShifts,
  validateMultipleDays,
  clearValidationCache,
  type DayShiftValidationResult,
  type UnifiedShiftValidationOptions
} from '@/lib/scheduling/unified-shift-validation-service';

export interface UseShiftValidationStatusOptions {
  businessId: string;
  includeOpeningHours?: boolean;
  enabled?: boolean;
}

export interface UseShiftValidationStatusResult {
  // Single day validation
  validateDay: (date: Date) => Promise<DayShiftValidationResult>;

  // Loading states
  isLoading: boolean;

  // Error handling
  error: string | null;

  // Cache management
  clearCache: () => void;

  // Refresh functionality
  refresh: () => void;
}

/**
 * Hook for validating shift status for individual days
 * Use this when you need on-demand validation for specific dates
 */
export const useShiftValidationStatus = (
  options: UseShiftValidationStatusOptions
): UseShiftValidationStatusResult => {
  const { businessId, includeOpeningHours = true, enabled = true } = options;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateDay = useCallback(async (date: Date): Promise<DayShiftValidationResult> => {
    if (!enabled || !businessId) {
      throw new Error('Validation disabled or missing business ID');
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await validateDayShifts(date, {
        businessId,
        includeOpeningHours
      });
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown validation error';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [businessId, includeOpeningHours, enabled]);

  const clearCache = useCallback(() => {
    clearValidationCache(businessId);
  }, [businessId]);

  const refresh = useCallback(() => {
    clearCache();
  }, [clearCache]);

  return {
    validateDay,
    isLoading,
    error,
    clearCache,
    refresh
  };
};

export interface UseMonthlyShiftValidationOptions {
  businessId: string;
  month: Date; // Any date within the month to validate
  includeOpeningHours?: boolean;
  includeEmployeeData?: boolean;
  enabled?: boolean;
  autoRefresh?: boolean; // Auto refresh on month change
}

export interface UseMonthlyShiftValidationResult {
  // Monthly validation results
  validationResults: Map<string, DayShiftValidationResult>;

  // Quick lookup functions
  getDayStatus: (date: Date) => DayShiftValidationResult | null;
  getDayStatusByKey: (dateKey: string) => DayShiftValidationResult | null;

  // Loading states
  isLoading: boolean;

  // Error handling
  error: string | null;

  // Cache management
  clearCache: () => void;
  refresh: () => void;

  // Statistics
  stats: {
    totalDays: number;
    greenDays: number;
    orangeDays: number;
    redDays: number;
  };
}

/**
 * Hook for validating shift status for an entire month
 * Use this for calendar components that display monthly views
 * Optimized for batch processing and caching
 */
export const useMonthlyShiftValidation = (
  options: UseMonthlyShiftValidationOptions
): UseMonthlyShiftValidationResult => {
  const { businessId, month, includeOpeningHours = true, includeEmployeeData = false, enabled = true, autoRefresh = true } = options;
  const [validationResults, setValidationResults] = useState<Map<string, DayShiftValidationResult>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate all days in the month
  const monthDays = useMemo(() => {
    if (!month) return [];
    return eachDayOfInterval({
      start: startOfMonth(month),
      end: endOfMonth(month)
    });
  }, [month]);

  // Calculate month statistics
  const stats = useMemo(() => {
    let greenDays = 0;
    let orangeDays = 0;
    let redDays = 0;

    validationResults.forEach((result) => {
      switch (result.status) {
        case 'green':
          greenDays++;
          break;
        case 'orange':
          orangeDays++;
          break;
        case 'red':
          redDays++;
          break;
      }
    });

    return {
      totalDays: monthDays.length,
      greenDays,
      orangeDays,
      redDays
    };
  }, [validationResults, monthDays.length]);

  // Load validation data for the month with progressive loading
  const loadMonthValidation = useCallback(async () => {
    if (!enabled || !businessId || monthDays.length === 0) {
      setValidationResults(new Map());
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Progressive loading: Load visible/current days first for faster perceived performance
      const today = new Date();
      const currentWeekStart = new Date(today);
      currentWeekStart.setDate(today.getDate() - today.getDay());
      const currentWeekEnd = new Date(currentWeekStart);
      currentWeekEnd.setDate(currentWeekStart.getDate() + 6);

      // Split days into priority groups
      const priorityDays = monthDays.filter(day =>
        day >= currentWeekStart && day <= currentWeekEnd
      );
      const otherDays = monthDays.filter(day =>
        day < currentWeekStart || day > currentWeekEnd
      );

      // Load priority days first (but keep loading state active)
      if (priorityDays.length > 0) {
        const priorityResults = await validateMultipleDays(priorityDays, {
          businessId,
          includeOpeningHours,
          includeEmployeeData
        });
        setValidationResults(priorityResults);
        // Don't end loading yet - wait for full month to load
      }

      // Then load remaining days
      if (otherDays.length > 0) {
        const remainingResults = await validateMultipleDays(otherDays, {
          businessId,
          includeOpeningHours,
          includeEmployeeData
        });

        // Merge with existing results
        setValidationResults(prev => {
          const merged = new Map(prev);
          remainingResults.forEach((value, key) => merged.set(key, value));
          return merged;
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown validation error';
      setError(errorMessage);
      console.error('Error loading monthly validation:', err);
    } finally {
      setIsLoading(false);
    }
  }, [businessId, monthDays, includeOpeningHours, includeEmployeeData, enabled]);

  // Auto-load when dependencies change
  useEffect(() => {
    if (autoRefresh) {
      loadMonthValidation();
    }
  }, [loadMonthValidation, autoRefresh]);

  // Quick lookup functions
  const getDayStatus = useCallback((date: Date): DayShiftValidationResult | null => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return validationResults.get(dateKey) || null;
  }, [validationResults]);

  const getDayStatusByKey = useCallback((dateKey: string): DayShiftValidationResult | null => {
    return validationResults.get(dateKey) || null;
  }, [validationResults]);

  const clearCache = useCallback(() => {
    clearValidationCache(businessId);
  }, [businessId]);

  const refresh = useCallback(() => {
    clearCache();
    loadMonthValidation();
  }, [clearCache, loadMonthValidation]);

  return {
    validationResults,
    getDayStatus,
    getDayStatusByKey,
    isLoading,
    error,
    clearCache,
    refresh,
    stats
  };
};

export interface UseLiveShiftValidationOptions {
  businessId: string;
  dates: Date[];
  includeOpeningHours?: boolean;
  includeEmployeeData?: boolean;
  enabled?: boolean;
  refreshInterval?: number; // Auto refresh interval in milliseconds (set to 0 to disable auto-refresh)
}

export interface UseLiveShiftValidationResult {
  validationResults: Map<string, DayShiftValidationResult>;
  getDayStatus: (date: Date) => DayShiftValidationResult | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refresh: () => void;
}

/**
 * Hook for live shift validation with auto-refresh
 * Use this for components that need real-time updates (like active dashboards)
 */
export const useLiveShiftValidation = (
  options: UseLiveShiftValidationOptions
): UseLiveShiftValidationResult => {
  const { businessId, dates, includeOpeningHours = true, includeEmployeeData = false, enabled = true, refreshInterval = 60000 } = options; // Default 1 minute refresh
  const [validationResults, setValidationResults] = useState<Map<string, DayShiftValidationResult>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadValidation = useCallback(async () => {
    if (!enabled || !businessId || dates.length === 0) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const results = await validateMultipleDays(dates, {
        businessId,
        includeOpeningHours,
        includeEmployeeData
      });
      setValidationResults(results);
      setLastUpdated(new Date());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown validation error';
      setError(errorMessage);
      console.error('Error in live validation:', err);
    } finally {
      setIsLoading(false);
    }
  }, [businessId, dates, includeOpeningHours, includeEmployeeData, enabled]);

  // Auto-refresh effect
  useEffect(() => {
    if (!enabled) return;

    loadValidation(); // Always do initial load when enabled

    // Only set up interval polling if refreshInterval > 0
    if (refreshInterval > 0) {
      const interval = setInterval(loadValidation, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [loadValidation, enabled, refreshInterval]);

  const getDayStatus = useCallback((date: Date): DayShiftValidationResult | null => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return validationResults.get(dateKey) || null;
  }, [validationResults]);

  const refresh = useCallback(() => {
    clearValidationCache(businessId);
    loadValidation();
  }, [businessId, loadValidation]);

  return {
    validationResults,
    getDayStatus,
    isLoading,
    error,
    lastUpdated,
    refresh
  };
};